import React, { SetStateAction, useEffect, useMemo } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { CountryCode } from "libphonenumber-js";
import { useAppSelector, RootState } from "../../../../store";
import { useGetABOEnrichmentDataMutation } from "../../../../services/AboEnrichment/AboEnrichmentAPI";
import { KPI_ID, type KpiId } from "../../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { ErrorTile } from "../../../../common/components/ErrorTile/ErrorTile";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { AmwayFilledLogoIcon } from "../../../../common/icons/AmwayFilledLogoIcon/AmwayFilledLogoIcon";
import { ReactComponent as PhoneSvg } from "../../../../common/icons/IconPhone/svg/phone.svg";
import { ReactComponent as MessageSvg } from "../../../../common/icons/IconMessage/svg/message.svg";
import { IconWhatsAppOutline } from "../../../../common/icons/IconWhatsAppOutline/IconWhatsAppOutline";
import { parsePhoneNumber } from "../../../../common/util/phoneNumber";
import { getCurrentPeriod } from "../../../../common/util/period";

import defaultContent from "../../../../content/downlineProfile";
import commonContent from "../../../../content/common";
import keyMetricsContent from "../../../../content/keyMetrics";
import "./LosLineage.scss";
import { IconChevronRight } from "../../../../common/icons/IconChevronRight/IconChevronRight";
import useWindowSize from "../../../../common/hooks/useWindowSize";
import type { AboLineageEntry } from "../../../../services/AboProfile/AboProfileAPI.types";
import { storedLocale } from "../../../../components/Authentication/util";
import useGetProfileImage from "../../../../components/DownlineProfile/hooks/useGetProfileImage";
import { IconRotatingCircle } from "../../../icons/IconRotatingCircle/IconRotatingCircle";
import maskAboNumber from "../../../util/maskAboNumber";

interface LosLineagePillProps {
  losNumber?: number;
  name?: string;
  abo?: string;
  aff?: string;
  ppv?: string;
  percent?: number;
  phoneNumber?: string;
  whatsAppNumber?: string;
  hideTop?: boolean;
  hideBottomAndSelected?: boolean;
  isPrivate?: boolean;
  setSelectedAffAbo?: React.Dispatch<SetStateAction<string>>;
  setShowLineage?: React.Dispatch<SetStateAction<boolean>>;
  pushToHistory?: (affAbo: string) => void;
  aboOnclick?: (affAbo: string) => void;
  imageUrl?: string;
  dotCount?: number;
}

export const LosLineagePill = ({
  losNumber,
  name,
  abo,
  aff,
  ppv,
  percent,
  phoneNumber,
  whatsAppNumber,
  hideTop = false,
  hideBottomAndSelected = false,
  isPrivate = false,
  setSelectedAffAbo = () => {},
  setShowLineage = () => {},
  pushToHistory = () => {},
  aboOnclick,
  imageUrl = "",
  dotCount = 4,
}: LosLineagePillProps) => {
  const { t } = useTranslation(["downlineProfile", "common", "keyMetrics"]);
  const { showCallIcon = true, showMessageIcon = true } =
    useAppSelector((state: RootState) => state.boot.configuration.profile) ?? {};
  const hideWhatsApp = useAppSelector((state: RootState) => state.boot.configuration.hideWhatsApp);
  const { user } = useAppSelector((state: RootState) => state.boot);
  const { formattedNumber } = parsePhoneNumber(phoneNumber, user.isoCountryCode as CountryCode);
  const whatsAppContactNumber = whatsAppNumber?.replace(/\D/g, "");
  const showWhatsAppIcon = !isPrivate && !hideWhatsApp && !!whatsAppContactNumber;

  return (
    <div className="losLineagePill">
      <div className="losLineagePill--number">
        <div className={`losLineagePill--number--dash ${hideTop ? "losLineagePill--number--dash--hide" : ""}`}>
          {[...Array(dotCount)].map((_, i) => (
            <div key={i}></div>
          ))}
        </div>
        <div className="losLineagePill--number--value">{losNumber ?? ""}</div>
        <div
          className={`losLineagePill--number--dash ${hideBottomAndSelected ? "losLineagePill--number--dash--hide" : ""}`}
        >
          {[...Array(dotCount)].map((_, i) => (
            <div key={i}></div>
          ))}
        </div>
      </div>
      <div className={`losLineagePill--info ${hideBottomAndSelected ? "losLineagePill--selected" : ""}`}>
        <div className="losLineagePill--info--icon">
          <>{imageUrl !== "" ? <img src={imageUrl} alt="" /> : <AmwayFilledLogoIcon />}</>
        </div>
        <div className="losLineagePill--info--content">
          <div className="losLineagePill--info--content--info">
            <button
              onClick={() => {
                if (aboOnclick) {
                  flushSync(() => setShowLineage(false));
                  aboOnclick(`${aff}-${abo}`);
                  return;
                }
                setSelectedAffAbo(`${aff}-${abo}`);
                setShowLineage((prev) => !prev);
                pushToHistory(`${aff}-${abo}`);
              }}
            >
              <p>{!isPrivate ? name : `${t("confidentialUser", commonContent.confidentialUser, { ns: "common" })}`}</p>
            </button>
            <div>
              <p>
                <span>{isPrivate ? maskAboNumber(abo ?? "") : abo}</span>
                <span>|</span>
                {!isPrivate && (
                  <>
                    <span>
                      {ppv} {`${t("ppv", keyMetricsContent.ppv, { ns: "keyMetrics" })}`}
                    </span>
                    <span>|</span>
                  </>
                )}

                {!isPrivate && <span>{percent}%</span>}
              </p>
            </div>
          </div>
          {!isPrivate && (formattedNumber || showWhatsAppIcon) && (
            <div className="losLineagePill--info--content--contact">
              {formattedNumber && showCallIcon && (
                <a
                  role="button"
                  aria-label={t("phoneCall", defaultContent.phoneCall)}
                  href={`tel:${formattedNumber ?? ""}`}
                  target="_parent"
                >
                  <PhoneSvg />
                </a>
              )}
              {formattedNumber && showMessageIcon && (
                <a
                  role="button"
                  aria-label={t("sms", defaultContent.sms)}
                  href={`sms:${formattedNumber ?? ""}`}
                  target="_parent"
                >
                  <MessageSvg />
                </a>
              )}
              {showWhatsAppIcon && (
                <a
                  role="button"
                  aria-label={t("whatsApp", defaultContent.whatsApp)}
                  href={`https://wa.me/${whatsAppContactNumber}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconWhatsAppOutline />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LosLineagePillSkeleton = ({
  hideTop = false,
  hideBottom = false,
  dotCount = 4,
}: {
  hideTop?: boolean;
  hideBottom?: boolean;
  dotCount?: number;
}) => {
  return (
    <div className="losLineagePill">
      <div className="losLineagePill--number">
        <div className={`losLineagePill--number--dash ${hideTop ? "losLineagePill--number--dash--hide" : ""}`}>
          {[...Array(dotCount)].map((_, i) => (
            <div key={i}></div>
          ))}
        </div>
        <div className="losLineagePill--number--value">
          <IconRotatingCircle variant="medium" />
        </div>
        <div className={`losLineagePill--number--dash ${hideBottom ? "losLineagePill--number--dash--hide" : ""}`}>
          {[...Array(dotCount)].map((_, i) => (
            <div key={i}></div>
          ))}
        </div>
      </div>
      <div className="losLineagePill--info">
        <div
          className="losLineagePill--info--icon rectangle-loader"
          style={{ backgroundColor: "transparent", flexShrink: 0, marginBottom: 0 }}
        />
        <div className="losLineagePill--info--content">
          <div className="losLineagePill--info--content--info">
            <LeafRectangleLoader isLoading height="1.25rem" width="8rem" margin="0" />
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <LeafRectangleLoader isLoading height="1.25rem" width="4rem" margin="0" />
              <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
                <LeafRectangleLoader isLoading height="1.25rem" width="3rem" margin="0" />
                <p>|</p>
                <LeafRectangleLoader isLoading height="1.25rem" width="2rem" margin="0" />
              </div>
            </div>
          </div>
          <div className="losLineagePill--info--content--contact">
            <LeafRectangleLoader isLoading height="1.25rem" width="7.75rem" margin="0" />
          </div>
        </div>
      </div>
    </div>
  );
};

const DEFAULT_LOS_LINEAGE_KPIS: KpiId[] = [KPI_ID.ABO_NAME, KPI_ID.PHONE_NUMBER, KPI_ID.WHATSAPP];

interface LosLineageProps {
  lineage?: AboLineageEntry[];
  setSelectedAffAbo?: React.Dispatch<SetStateAction<string>>;
  setShowLineage?: React.Dispatch<SetStateAction<boolean>>;
  pushToHistory?: (affAbo: string) => void;
  aboOnclick?: (affAbo: string) => void;
  compact?: boolean;
  kpis?: KpiId[];
}

const LosLineage = ({
  lineage,
  setSelectedAffAbo,
  setShowLineage = () => {},
  pushToHistory,
  aboOnclick,
  compact = false,
  kpis = DEFAULT_LOS_LINEAGE_KPIS,
}: LosLineageProps) => {
  const { t } = useTranslation(["downlineProfile"]);
  const { isoCountryCode } = useAppSelector((state) => state.boot.user);
  const { width } = useWindowSize();
  const dotCount = (width ?? 0) <= 562 ? 5 : 4;
  const [getEnrichment, { data: enrichmentData, isLoading: isEnrichmentLoading, isError }] =
    useGetABOEnrichmentDataMutation();

  const affAbos = useMemo(
    () => (lineage ?? []).map(({ aff, abo }) => `${String(aff).padStart(3, "0")}-${abo}`),
    [lineage],
  );

  const profileImageEntries = useMemo(() => {
    return (
      lineage?.map((e) => {
        return { affAbo: `${e.aff}-${e.abo}`, imageRefUrl: e.imageRefUrl ?? "" };
      }) ?? []
    );
  }, [lineage]);

  const { profileImages, isLoading } = useGetProfileImage(profileImageEntries, false);

  useEffect(() => {
    if (affAbos.length > 0) {
      getEnrichment({
        affAbos,
        bonusPeriod: getCurrentPeriod(),
        locale: storedLocale(),
        isoCountryCode,
        kpis,
      });
    }
  }, [affAbos, getEnrichment, isoCountryCode, kpis]);

  return (
    <>
      <div className={`losLineage${compact ? " losLineage--compact" : ""}`}>
        <div className="losLineage--header">
          <button onClick={() => setShowLineage(false)}>
            <IconChevronRight color="blue" />
            <IconChevronRight color="blue" />
          </button>
          <p>{t("uplineLos", defaultContent.uplineLos)}</p>
        </div>
        <div className={`losLineage--pills${compact ? " losLineage--pills--compact" : ""}`}>
          {!isError ? (
            isEnrichmentLoading || isLoading ? (
              [...Array(5)].map((_, i) => (
                <LosLineagePillSkeleton key={i} hideTop={i === 0} hideBottom={i === 4} dotCount={dotCount} />
              ))
            ) : (
              (lineage ?? []).map((entry, i) => {
                const kpiData = enrichmentData?.aboKpiData?.find(
                  (k) => k.abo === String(entry.abo) && k.aff === String(entry.aff).padStart(3, "0"),
                );
                const imageUrl =
                  profileImages?.find((e) => {
                    return e.abo === String(entry.abo) && e.aff.padStart(3, "0") === String(entry.aff).padStart(3, "0");
                  })?.profileImage ?? "";
                return (
                  <LosLineagePill
                    key={entry.abo}
                    losNumber={entry.losNumber}
                    name={kpiData?.aboName ?? ""}
                    abo={String(entry.abo)}
                    aff={String(entry.aff)}
                    ppv={entry.ppv ?? "0"}
                    percent={entry.bonusPercent ?? 0}
                    phoneNumber={kpiData?.phoneNumber ?? ""}
                    whatsAppNumber={kpiData?.whatsapp ?? ""}
                    hideTop={i === 0}
                    hideBottomAndSelected={i === (lineage?.length ?? 0) - 1}
                    setSelectedAffAbo={setSelectedAffAbo}
                    setShowLineage={setShowLineage}
                    isPrivate={kpiData?.aboName === "Confidential User"}
                    pushToHistory={pushToHistory}
                    aboOnclick={aboOnclick}
                    imageUrl={imageUrl}
                    dotCount={dotCount}
                  />
                );
              })
            )
          ) : (
            <ErrorTile
              errorStatus={isError}
              isLoading={isEnrichmentLoading}
              clickFunction={() =>
                getEnrichment({
                  affAbos,
                  bonusPeriod: getCurrentPeriod(),
                  locale: storedLocale(),
                  isoCountryCode,
                  kpis,
                })
              }
              tealiumTrackId="losLineage"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default LosLineage;
